import {
  Box,
  ConfirmationDialogBox,
  IconButton,
  NumericInput,
  Table,
  TableBody,
  TableCell,
  TableFoot as TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, PlusCircleOutlineIcon } from '@atgfr8/icons';
import { currencyTypes } from '@common/commonLists';
import { NEW_LINE_ITEM } from '@common/order/constants';
import { isInNegativeCreditWarningZone } from '@common/order/creditLimit';
import {
  BasicModal,
  MenuItemType,
  SelectInput,
  SelectInputSearchable,
  TableRowErrors,
} from '@components';
import { EmptyData } from '@components/EmptyData/EmptyData';
import { FeatureFlags } from '@constants/featureFlags';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { openModal } from '@store/modals';
import {
  computeTotalLineItems,
  customerStore,
  ediSettingsValues,
  orderContainsLTLLoad,
  orderStore,
  updateOrderFormErrorState,
  updateOrderState,
  validateFieldForOrder,
} from '@store/orders';
import { isFalveyInsuranceCommitted } from '@store/orders/helper';
import { OrderLineItem } from '@typeDefinitions/lineItemTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  capitalizeWord,
  formatAmount,
  groupByDisabledHeaders,
  isAdmin,
  isSuperAdmin,
  orderTableErrors,
  trimDecimalValue,
} from '@utils/utils';
import { DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID } from '@views/order/constants';
import { Accessor, Index, Show, createEffect, createSignal } from 'solid-js';

import { removeTableRow } from '../../carrier/stops/stopsEditor/utilFunctions';
import {
  CreditAcknowledgementModalId,
  LineItemsLockedModel,
} from '../../models';
import classes from './classes';

export const CustomerPay = () => {
  createEffect(() => {
    if (
      orderStore.order.lineItems.length === 0 &&
      orderStore.isCreate &&
      Boolean(orderStore.order.customerId)
    ) {
      addRow();
      updateLineItems('type', 'Line Haul', 0);
    }
  });
  const [deleteRow, setDeleteRow] = createSignal<number | null>(null);

  const editLineItems = 'editLineItems';

  const checkCreditAcknowledgement = () => {
    if (
      !isFeatureFlagEnabled(
        FeatureFlags.feature_order_block_save_on_neg_credit_limit,
      )()
    ) {
      return;
    }
    const { available, negativeCreditAllowance } = customerStore.customer;
    if (
      isInNegativeCreditWarningZone({
        available:
          (orderStore.order.customer?.existingCustomerCredit ?? available) +
          (orderStore.order.existingLineItemsTotal ?? 0),
        negativeCreditAllowance,
        lineItemsTotal: computeTotalLineItems(),
      }) &&
      !orderContainsLTLLoad()
    ) {
      openModal(CreditAcknowledgementModalId);
    }
  };

  //TODO: MAKE REUSABLE FUNCTION SIMILAR TO REMOVE ROW FROM TABLE
  const addRow = () => {
    const lineItems = [...orderStore.order.lineItems];
    lineItems.push(NEW_LINE_ITEM);
    updateOrderState({ lineItems: lineItems });
  };

  const getHeaders = () => {
    return orderStore.isMobile
      ? ['Type', 'Desc.', 'Qty', 'Rate']
      : ['', 'Type', 'Desc.', 'Qty', 'Rate', 'Total'];
  };

  const cellStyles = {
    padding: '1px 1px',
    'border-bottom': '0px !important',
    'vertical-align': 'top',
  };

  const getRowBorderCellClass = (index: number) =>
    index === 0 ? '' : 'border-t! border-[#a8c5bf]!';

  //TODO: MAKE REUSABLE FUNCTION SIMILAR TO REMOVE ROW FROM TABLE
  const updateLineItems = (
    key: string,
    value: string | number,
    index: number,
  ) => {
    const lineItems = [...orderStore.order.lineItems];
    let opType = 'Insert' as OperationTypeV4;
    if (lineItems[index].id > 0) {
      opType = 'Update' as OperationTypeV4;
    }
    lineItems[index] = {
      ...lineItems[index],
      [key]: value,
      operationType: opType,
    };
    updateOrderState({ lineItems: lineItems });
  };

  let filteredOptions: MenuItemType[] = [];
  for (const prop in ediSettingsValues.ediSettings) {
    if (
      Object.prototype.hasOwnProperty.call(ediSettingsValues.ediSettings, prop)
    ) {
      filteredOptions.push({
        label: capitalizeWord(prop),
        value: prop,
        disabled: true,
      });
      const filteredOthers = (
        ediSettingsValues.ediSettings[
          prop as keyof typeof ediSettingsValues.ediSettings
        ] as MenuItemType[]
      )
        .map((e) => {
          return { label: e.label, value: e.value } as MenuItemType;
        })
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
      filteredOptions = filteredOptions.concat(filteredOthers);
    }
  }

  function isLineItemRateLocked(item: OrderLineItem): boolean {
    if (item.synched || orderStore.isReadOnly) {
      return true;
    }
    if (isAdmin()) {
      return false;
    }
    return item.type === 'LTL Insurance' && isFalveyInsuranceCommitted();
  }

  return (
    <>
      {(customerStore.customer.showCustomerCurrencyTypeEnabled ||
        customerStore.customer.invoiceMethod === 'Canadian') && (
        <Box class="flex items-center justify-end gap-3 ">
          <Typography variant="body1">Customer Currency Type</Typography>
          <div class="w-[160px]">
            <SelectInput
              name="currencyType"
              label=""
              menuItems={currencyTypes.map((c) => ({ label: c, value: c }))}
              value={orderStore.order.currencyType ?? ''}
              size="md"
              onChange={(val) => {
                updateOrderState({ currencyType: val });
              }}
              style={{ width: '160px' }}
              disabled={
                orderStore.isReadOnly ||
                (orderStore.order.status === 'Paid' &&
                  !isAdmin() &&
                  !isSuperAdmin())
              }
              dataTestId={formatDataTestId(
                'Customer currency type',
                'selectInput',
              )}
            />
          </div>
        </Box>
      )}
      <div>
        <Table>
          <TableHead>
            <TableRow>
              {getHeaders().map((item) => (
                <TableHeaderCell class="text-black font-medium text-[12px]">
                  {item}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <Show
            when={
              orderStore.order.lineItems.filter(
                (v) => v.operationType !== 'Delete',
              ).length === 0
            }
          >
            <TableBody class="w-full nonMobile:h-64 bg-[#BFDFD8]">
              <TableRow>
                <TableCell
                  colSpan={getHeaders().length + 1}
                  style={{ border: 0 }}
                >
                  <Show when={!orderStore.isMobile}>
                    <EmptyData />
                  </Show>
                </TableCell>
              </TableRow>
            </TableBody>
          </Show>
          <Show when={orderStore.order.lineItems.length > 0}>
            <TableBody class={classes.tableBg}>
              <Index each={orderStore.order.lineItems}>
                {/* eslint-disable-next-line complexity */}
                {(item: Accessor<OrderLineItem>, index) => (
                  <Show when={item().operationType !== 'Delete'}>
                    <TableRow
                      class={`${classes.tableRow} ${
                        Boolean(item().synched)
                          ? 'cursor-not-allowed'
                          : 'cursor-default'
                      }`}
                    >
                      <Show when={!orderStore.isMobile}>
                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={{ padding: '0px', 'vertical-align': 'top' }}
                        >
                          <Show when={item().type !== 'LTL Insurance'}>
                            <IconButton
                              dataTestId="delete-customer-pay-line-item-button"
                              disabled={
                                item().synched ||
                                item().type === 'LTL Insurance' ||
                                orderStore.isReadOnly
                              }
                              onClick={() => {
                                setDeleteRow(index);
                                openDialogBox(
                                  DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID,
                                );
                              }}
                            >
                              <DeleteIcon
                                class={classes.deleteIcon}
                                size={20}
                              />
                            </IconButton>
                          </Show>
                        </TableCell>
                      </Show>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ ...cellStyles, 'min-width': '100px' }}
                      >
                        <div class="mobile:w-[110px] nonMobile:w-full">
                          <SelectInputSearchable
                            dataTestId={`type-select-${index}`}
                            label=""
                            placeholder=""
                            size="md"
                            value={item().type as string}
                            menuItems={groupByDisabledHeaders(filteredOptions)}
                            onChange={(value: string) => {
                              updateLineItems('type', value, index);
                              updateOrderFormErrorState({
                                ...orderStore.orderFormError,
                                [`lineItems[${index}].type`]: undefined,
                              });
                            }}
                            onBlur={() =>
                              validateFieldForOrder(
                                `lineItems.${index}.type`,
                                `lineItems[${index}].type`,
                              )
                            }
                            error={
                              orderStore.orderFormError?.[
                                `lineItems[${index}].type`
                              ] as string | undefined
                            }
                            disabled={
                              item().synched ||
                              item().type === 'LTL Insurance' ||
                              orderStore.isReadOnly
                            }
                          />
                          {/* <SelectInput
                            dataTestId={`type-select-${index}`}
                            placeholder="Select Type"
                            size="md"
                            value={item().type}
                            menuItems={filteredOptions.map((option) => ({
                              label: option.label as string,
                              value: option.value as string,
                              disabled: option.disabled,
                            }))}
                            onChange={(value: string) => {
                              updateLineItems('type', value, index);
                              updateOrderFormErrorState({
                                ...orderStore.orderFormError,
                                [`lineItems[${index}].type`]: undefined,
                              });
                            }}
                            onBlur={() =>
                              validateFieldForOrder(
                                `lineItems.${index}.type`,
                                `lineItems[${index}].type`,
                              )
                            }
                            error={
                              orderStore.orderFormError?.[
                                `lineItems[${index}].type`
                              ] as string | undefined
                            }
                            disabled={
                              item().synched ||
                              item().type === 'LTL Insurance' ||
                              orderStore.isReadOnly
                            }
                          /> */}
                        </div>
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ ...cellStyles }}
                      >
                        <div class="mobile:w-[90px] nonMobile:w-full">
                          <TextField
                            value={item().description}
                            onChange={(value: string) => {
                              updateLineItems('description', value, index);
                            }}
                            class={'bg-white rounded-sm h-[40px]!'}
                            label=""
                            disabled={
                              item().synched ||
                              item().type === '' ||
                              item().type === 'LTL Insurance' ||
                              orderStore.isReadOnly
                            }
                            placeholder="Description"
                            size="sm"
                            dataTestId={`description-textInput-${index}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ ...cellStyles }}
                      >
                        <div class="mobile:w-[40px] nonMobile:w-full">
                          <NumericInput
                            label=""
                            placeholder="Qty"
                            value={item().quantity}
                            onChange={(value: string) => {
                              updateLineItems('quantity', value, index);
                              updateOrderFormErrorState({
                                ...orderStore.orderFormError,
                                [`lineItems[${index}].quantity`]: undefined,
                              });
                            }}
                            size="sm"
                            on:blur={() => {
                              void validateFieldForOrder(
                                `lineItems.${index}.quantity`,
                                `lineItems[${index}].quantity`,
                              );
                              checkCreditAcknowledgement();
                            }}
                            error={Boolean(
                              orderStore.orderFormError &&
                              orderStore.orderFormError[
                                `lineItems[${index}].quantity`
                              ],
                            )}
                            class={'bg-white rounded-sm h-[40px]!'}
                            disabled={
                              item().synched ||
                              item().type === 'LTL Insurance' ||
                              orderStore.isReadOnly
                            }
                            dataTestId={`quantity-textInput-${index}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        class={getRowBorderCellClass(index)}
                        style={{ ...cellStyles }}
                      >
                        <div class="mobile:w-[60px] nonMobile:w-full">
                          <NumericInput
                            placeholder="Rate"
                            value={item().rate as string | number}
                            onChange={(value: string) => {
                              const val = trimDecimalValue(value, 7, 6, true);
                              updateLineItems('rate', val, index);
                              updateOrderFormErrorState({
                                ...orderStore.orderFormError,
                                [`lineItems[${index}].rate`]: undefined,
                              });
                            }}
                            on:blur={() => {
                              void validateFieldForOrder(
                                `lineItems.${index}.rate`,
                                `lineItems[${index}].rate`,
                              );
                              checkCreditAcknowledgement();
                            }}
                            error={Boolean(
                              orderStore.orderFormError &&
                              orderStore.orderFormError[
                                `lineItems[${index}].rate`
                              ],
                            )}
                            class={'bg-white rounded-sm h-[40px]!'}
                            label=""
                            disabled={isLineItemRateLocked(item())}
                            size="sm"
                            dataTestId={`rate-textInput-${index}`}
                          />
                        </div>
                      </TableCell>
                      <Show when={!orderStore.isMobile}>
                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={{ ...cellStyles, 'vertical-align': 'middle' }}
                        >
                          <Typography
                            variant="body2"
                            class={`${classes.totalAmount} font-normal!`}
                          >
                            {formatAmount(
                              Number(
                                !item().quantity ||
                                  isNaN(Number(item().quantity)) ||
                                  Number(item().quantity) === 0
                                  ? '0'
                                  : item().quantity,
                              ) *
                                Number(
                                  item().rate === '' ||
                                    item().rate === '.' ||
                                    item().rate === '-'
                                    ? '0'
                                    : item().rate,
                                ),
                            )}
                          </Typography>
                        </TableCell>
                      </Show>
                    </TableRow>
                    <Show when={orderStore.isMobile}>
                      <TableRow>
                        <TableCell>
                          <div>Total :</div>
                        </TableCell>
                        <TableCell
                          style={cellStyles}
                          colSpan={getHeaders().length}
                        >
                          <Typography
                            variant="body2"
                            class={`${classes.totalAmount} font-normal!`}
                          >
                            {formatAmount(
                              Number(
                                !item().quantity ||
                                  isNaN(Number(item().quantity)) ||
                                  Number(item().quantity) === 0
                                  ? '0'
                                  : item().quantity,
                              ) *
                                Number(
                                  item().rate === '' ||
                                    item().rate === '.' ||
                                    item().rate === '-'
                                    ? '0'
                                    : item().rate,
                                ),
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </Show>
                    <TableRowErrors
                      columnsLength={getHeaders().length}
                      tableErrors={orderTableErrors(
                        ['rate', 'quantity', 'type', 'description'],
                        'lineItems[{index}].{field}',
                        index,
                      )}
                    />
                  </Show>
                )}
              </Index>
            </TableBody>
          </Show>
          <TableFooter class="text-base! font-normal! font-weight-700! leading-20!.02 tracking-0!.17 text-black! bg-[#BFDFD8]!">
            <Show when={computeTotalLineItems() < 0}>
              <TableRowErrors
                columnsLength={getHeaders().length}
                tableErrors={[
                  'Rate changes cannot result in a negative total amount.',
                ]}
              />
            </Show>
            <TableRow class="bg-[#BFDFD8]!">
              <TableCell
                class="border-t! border-[#a8c5bf]!"
                colSpan={getHeaders().length}
              >
                <div class="flex items-center justify-between">
                  <IconButton
                    dataTestId="add-customer-pay-line-item-button"
                    onClick={addRow}
                    class="p-0!"
                    disabled={
                      (orderStore.isCreate &&
                        !Boolean(orderStore.order.customerId)) ||
                      ((orderStore.order.status === 'Paid' ||
                        orderStore.order.status === 'Invoiced') &&
                        (!isAdmin() || !isSuperAdmin()))
                    }
                  >
                    <PlusCircleOutlineIcon size={20} class={classes.addIcon} />
                  </IconButton>
                  <div class="flex items-center gap-x-2">
                    <Typography variant="body2" class={classes.totalLabel}>
                      Total
                    </Typography>
                    <Typography variant="body2" class={classes.totalAmount}>
                      {formatAmount(computeTotalLineItems())}
                    </Typography>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <Show when={orderStore.order.synched}>
        <Box class="mt-2">
          <span
            class={classes.editLineItems}
            onClick={() => openModal(editLineItems)}
          >
            Why can’t I edit my Line Items?
          </span>
        </Box>
      </Show>
      <BasicModal
        id={editLineItems}
        width={'800px'}
        footer={false}
        header={false}
      >
        <LineItemsLockedModel />
      </BasicModal>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID,
        )}
        dataTestId={DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID}
        id={DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID}
        title={'Are you sure you want to delete this line item?'}
        onSubmit={() => {
          removeTableRow(deleteRow()!, orderStore.order, 'order', 'lineItems');
          setDeleteRow(null);
        }}
        onSubmitText="Delete"
        closeDialogBox={(_reason) => {
          closeDialogBox(DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID);
          setDeleteRow(null);
        }}
      />
    </>
  );
};
